import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, DollarSign, Save, Shield, UsersRound } from 'lucide-react';
import { paths } from '../lib/paths';
import { type CompanyLead, formatDate, intentLabel, timelineLabel } from '../lib/companyLeads';

type SubscriptionMode = 'self_serve' | 'contract_managed';
type BillingCollectionMethod = 'charge_automatically' | 'send_invoice';

export function CompanyCustomersPage() {
  const [leads, setLeads] = useState<CompanyLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/contact/list', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        const data = (await r.json()) as { submissions: CompanyLead[] };
        setLeads(data.submissions);
      })
      .catch(() => setError('Could not load customers.'))
      .finally(() => setLoading(false));
  }, []);

  const customers = leads.filter((lead) => lead.status === 'won');

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="card" style={{ padding: 24 }}>
        <div className="eyebrow">Scorekeeper HQ</div>
        <h1 style={{ margin: '6px 0 0', fontSize: 30 }}>Customers</h1>
        <p className="muted" style={{ maxWidth: 760, marginTop: 10 }}>
          V1 treats won pipeline leads as customer leagues. This keeps the
          company workflow lightweight until onboarding needs justify a full
          customer account model.
        </p>
        <div className="row" style={{ gap: 10, marginTop: 18 }}>
          <Link to={paths.companyPipeline} className="btn btn--primary">
            Manage pipeline
          </Link>
          <Link to={paths.companyHome} className="btn">
            Dashboard
          </Link>
        </div>
      </section>

      <div className="grid grid--2" style={{ gap: 16 }}>
        <section className="card" style={{ padding: 22 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h3 className="card__title" style={{ margin: 0 }}>
              <UsersRound size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
              Won leads
            </h3>
            {!loading && <span className="chip chip--ok">{customers.length} customer{customers.length === 1 ? '' : 's'}</span>}
          </div>

          {loading ? (
            <p className="muted">Loading customers...</p>
          ) : error ? (
            <p style={{ color: 'var(--color-danger, #e53e3e)' }}>{error}</p>
          ) : customers.length === 0 ? (
            <div className="empty-state" style={{ padding: '30px 0' }}>
              <p className="muted">No won leads yet. Mark a pipeline lead as Won to show it here.</p>
            </div>
          ) : (
            <div className="stack" style={{ gap: 12, marginTop: 14 }}>
              {customers.map((customer) => (
                <div key={customer.id} className="card" style={{ padding: 16, background: 'var(--surface-2, var(--surface))' }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{customer.organization_name || customer.name}</div>
                      <div className="muted" style={{ fontSize: 13 }}>
                        {customer.name} - {customer.email}
                      </div>
                    </div>
                    <span className="chip chip--ok">Won</span>
                  </div>
                  <div className="grid grid--2" style={{ gap: 10, marginTop: 12 }}>
                    <MiniFact label="Intent" value={intentLabel(customer.intent)} />
                    <MiniFact label="League size" value={customer.league_size || 'Not specified'} />
                    <MiniFact label="Timeline" value={timelineLabel(customer.timeline)} />
                    <MiniFact label="Won from" value={formatDate(customer.created_at)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="stack" style={{ gap: 16 }}>
          <PricingTermsCard />
          <div className="card" style={{ padding: 22 }}>
            <h3 className="card__title" style={{ margin: 0 }}>
              <DollarSign size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
              Next customer layer
            </h3>
            <p className="muted" style={{ marginTop: 8 }}>
              Later this can become onboarding status, account health, renewal
              notes, and league-specific implementation tasks.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function MiniFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="stat__label">{label}</div>
      <div style={{ fontWeight: 700, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function PricingTermsCard() {
  const [monthly, setMonthly] = useState('');
  const [feePct, setFeePct] = useState('');
  const [feeFlat, setFeeFlat] = useState('');
  const [subscriptionMode, setSubscriptionMode] = useState<SubscriptionMode>('self_serve');
  const [collectionMethod, setCollectionMethod] = useState<BillingCollectionMethod>('charge_automatically');
  const [notes, setNotes] = useState('');
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/stripe/terms', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as {
          terms: {
            subscription_monthly_cents: number;
            platform_fee_bps: number;
            platform_fee_flat_cents: number;
            subscription_mode?: SubscriptionMode;
            collection_method?: BillingCollectionMethod;
            notes: string | null;
            updated_at: string;
          };
        };
        setMonthly((data.terms.subscription_monthly_cents / 100).toFixed(2));
        setFeePct((data.terms.platform_fee_bps / 100).toFixed(2));
        setFeeFlat((data.terms.platform_fee_flat_cents / 100).toFixed(2));
        setSubscriptionMode(data.terms.subscription_mode ?? 'self_serve');
        setCollectionMethod(data.terms.collection_method ?? 'charge_automatically');
        setNotes(data.terms.notes ?? '');
        setLoadedAt(data.terms.updated_at);
      })
      .catch(() => undefined);
  }, []);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const body = {
        subscriptionMonthlyCents: Math.round(parseFloat(monthly) * 100),
        platformFeeBps: Math.round(parseFloat(feePct) * 100),
        platformFeeFlatCents: Math.round(parseFloat(feeFlat) * 100),
        subscriptionMode,
        collectionMethod,
        notes: notes.trim() || null,
      };
      const res = await fetch('/api/stripe/terms', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const ej = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(ej.error ?? `request failed (${res.status})`);
      }
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 22, borderColor: 'var(--accent)' }}>
      <div className="card__head">
        <div>
          <h3 className="card__title">
            <Shield size={16} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--accent)' }} />
            Internal pricing terms
          </h3>
          <p className="card__sub">
            Super-admin pricing controls for the current deployed league.
            {loadedAt ? ` Last saved ${new Date(loadedAt).toLocaleString()}.` : ''}
          </p>
        </div>
      </div>

      {err && (
        <div className="row" style={{ gap: 8, marginBottom: 10, color: 'var(--warn)' }}>
          <AlertTriangle size={14} /> <span style={{ fontSize: 13 }}>{err}</span>
        </div>
      )}

      <form onSubmit={onSave} className="stack" style={{ gap: 14 }}>
        <div className="grid grid--3" style={{ gap: 12 }}>
          <label className="field">
            <span className="field__label">Monthly fee</span>
            <input type="number" step="0.01" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)} className="input" />
          </label>
          <label className="field">
            <span className="field__label">Take rate (%)</span>
            <input type="number" step="0.01" min="0" max="20" value={feePct} onChange={(e) => setFeePct(e.target.value)} className="input" />
          </label>
          <label className="field">
            <span className="field__label">Flat fee per reg</span>
            <input type="number" step="0.01" min="0" value={feeFlat} onChange={(e) => setFeeFlat(e.target.value)} className="input" />
          </label>
        </div>
        <div className="grid grid--2" style={{ gap: 12 }}>
          <label className="field">
            <span className="field__label">Subscription mode</span>
            <select
              className="select"
              value={subscriptionMode}
              onChange={(event) => setSubscriptionMode(event.target.value as SubscriptionMode)}
            >
              <option value="self_serve">Self-serve activation</option>
              <option value="contract_managed">Contract managed</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Collection method</span>
            <select
              className="select"
              value={collectionMethod}
              onChange={(event) => setCollectionMethod(event.target.value as BillingCollectionMethod)}
            >
              <option value="charge_automatically">Automatic card/bank charge</option>
              <option value="send_invoice">Stripe invoice</option>
            </select>
          </label>
        </div>
        <div className="note note--soft">
          Use self-serve with automatic collection for standard leagues. Use contract managed when
          The Scorekeeper creates the subscription or invoice outside the league UI.
        </div>
        <label className="field">
          <span className="field__label">Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="textarea" placeholder="Intro rate, renewal note, or customer context" />
        </label>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
            <Save size={12} /> {saving ? 'Saving...' : 'Save terms'}
          </button>
          {savedAt && <span className="muted" style={{ fontSize: 12 }}>Saved {savedAt}</span>}
        </div>
      </form>
    </div>
  );
}
